import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PlacementLink } from "../PlacementLink";
import { Skeleton } from "../ui/skeleton";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Eye, MousePointerClick, TrendingUp, BarChart3, CalendarCheck } from "lucide-react";
import { PLACEMENT_LABEL, fmtMonth } from "./adShared";

export function RevenueSection({ refreshKey }) {
  const [revenue, setRevenue] = useState(null);
  useEffect(() => { api.get("/admin/ad-revenue").then((r) => setRevenue(r.data)).catch(() => {}); }, [refreshKey]);
  return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mt-6" data-testid="revenue-dashboard">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><BarChart3 size={18} className="text-[#F5551E]" /> Ad revenue</h3>
          {revenue && <span className="text-sm font-semibold text-emerald-600" data-testid="revenue-total">{revenue.currency} ${revenue.total.toFixed(2)} total{revenue.best_placement ? ` · top: ${PLACEMENT_LABEL[revenue.best_placement] || revenue.best_placement}` : ""}</span>}
        </div>
        {!revenue ? (
          <Skeleton className="h-28 rounded-xl" />
        ) : revenue.count === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No ad revenue yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Monthly trend</p>
              <div className="h-44 -ml-2" data-testid="revenue-by-month">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenue.by_month.map((m) => ({ ...m, label: fmtMonth(m.month) }))} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      formatter={(v) => [`${revenue.currency} $${Number(v).toFixed(2)}`, "Revenue"]}
                      labelStyle={{ color: "#12234A", fontWeight: 600 }}
                      contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                    />
                    <Line type="monotone" dataKey="amount" stroke="#F5551E" strokeWidth={2.5} dot={{ r: 3, fill: "#F5551E" }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">By placement</p>
              <div className="space-y-2" data-testid="revenue-by-placement">
                {(() => { const max = Math.max(...revenue.by_placement.map((p) => p.amount), 1); return revenue.by_placement.map((p) => (
                  <div key={p.placement} className="flex items-center gap-3">
                    <span className="text-xs text-slate-500 w-16 shrink-0"><PlacementLink placement={p.placement} className="text-slate-500" newTab /></span>
                    <div className="flex-1 h-4 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-[#F5551E]" style={{ width: `${(p.amount / max) * 100}%` }} /></div>
                    <span className="text-xs font-semibold text-[#12234A] w-20 text-right shrink-0">{revenue.currency} ${p.amount.toFixed(2)}</span>
                  </div>
                )); })()}
              </div>
            </div>
          </div>
        )}
      </div>
  );
}

export function OccupancySection({ refreshKey }) {
  const [occupancy, setOccupancy] = useState(null);
  useEffect(() => { api.get("/admin/ads/occupancy").then((r) => setOccupancy(r.data)).catch(() => {}); }, [refreshKey]);
  return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mt-6" data-testid="occupancy-section">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><CalendarCheck size={18} className="text-[#F5551E]" /> Slot occupancy — next 30 days</h3>
          {occupancy && <span className="text-sm font-semibold text-slate-500" data-testid="occupancy-summary">{occupancy.total_booked} booked · <span className="text-amber-600">{occupancy.total_empty} empty</span> of {occupancy.total_slot_days} slot-days</span>}
        </div>
        <p className="text-[11px] text-slate-400 mb-4">How many of the next 30 days each slot has a paid booking. Chase the slots with the most empty days to fill unsold inventory.</p>
        {!occupancy ? (
          <Skeleton className="h-28 rounded-xl" />
        ) : (
          <div className="space-y-3" data-testid="occupancy-list">
            {occupancy.placements.map((p) => (
              <div key={p.placement} className="flex items-center gap-3" data-testid={`occupancy-row-${p.placement}`}>
                <span className="text-xs font-semibold text-[#12234A] w-28 shrink-0"><PlacementLink placement={p.placement} className="text-[#12234A]" newTab /></span>
                <div className="flex-1 h-5 rounded-full bg-amber-100 overflow-hidden flex">
                  <div className="h-full bg-emerald-500 flex items-center justify-end pr-1.5" style={{ width: `${p.occupancy}%` }}>
                    {p.occupancy >= 18 && <span className="text-[10px] font-bold text-white">{p.booked_days}d</span>}
                  </div>
                </div>
                <span className="text-xs w-40 text-right shrink-0">
                  <span className={`font-bold ${p.occupancy >= 70 ? "text-emerald-600" : p.occupancy >= 30 ? "text-amber-600" : "text-red-500"}`}>{p.occupancy}%</span>
                  <span className="text-slate-400"> · {p.empty_days} empty</span>
                </span>
              </div>
            ))}
            <div className="flex items-center gap-3 pt-1 text-[11px] text-slate-400">
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Booked</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-200 inline-block" /> Empty</span>
            </div>
          </div>
        )}
      </div>
  );
}

export function AnalyticsSection({ rates }) {
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => { api.get("/admin/ads/placement-analytics").then((r) => setAnalytics(r.data)).catch(() => {}); }, []);
  return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mt-6" data-testid="placement-analytics-section">
        <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-1"><TrendingUp size={18} className="text-[#F5551E]" /> Placement demand</h3>
        <p className="text-[11px] text-slate-400 mb-4">How advertisers engage with each slot — previews, picks and live bookings — to help you tune pricing.</p>
        {!analytics ? (
          <Skeleton className="h-24 rounded-xl" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="placement-analytics-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left border-b border-slate-100">
                  <th className="py-2 pr-4 font-semibold">Placement</th>
                  <th className="py-2 px-3 font-semibold"><span className="inline-flex items-center gap-1"><Eye size={12} /> Previews</span></th>
                  <th className="py-2 px-3 font-semibold"><span className="inline-flex items-center gap-1"><MousePointerClick size={12} /> Picks</span></th>
                  <th className="py-2 px-3 font-semibold">Pick rate</th>
                  <th className="py-2 px-3 font-semibold">Submitted</th>
                  <th className="py-2 px-3 font-semibold">Live now</th>
                </tr>
              </thead>
              <tbody>
                {analytics.placements.map((p) => (
                  <tr key={p.placement} className="border-b border-slate-50" data-testid={`analytics-row-${p.placement}`}>
                    <td className="py-2 pr-4 font-semibold text-[#12234A]"><PlacementLink placement={p.placement} className="text-[#12234A]" newTab /> <span className="text-[10px] font-normal text-[#F5551E]">${rates?.rates?.[p.placement]}/day</span></td>
                    <td className="py-2 px-3 text-slate-600">{p.previews.toLocaleString()}</td>
                    <td className="py-2 px-3 text-slate-600">{p.picks.toLocaleString()}</td>
                    <td className="py-2 px-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.conversion >= 50 ? "bg-emerald-100 text-emerald-700" : p.conversion > 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{p.conversion}%</span></td>
                    <td className="py-2 px-3 text-slate-600">{p.submissions.toLocaleString()}</td>
                    <td className="py-2 px-3 font-semibold text-[#12234A]">{p.live.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-slate-400 mt-3">Total previews: <strong className="text-[#12234A]">{analytics.total_previews.toLocaleString()}</strong> · Total picks: <strong className="text-[#12234A]">{analytics.total_picks.toLocaleString()}</strong></p>
          </div>
        )}
      </div>
  );
}
